import { useState } from 'react'

import { IconPaperclip } from '@tabler/icons-react'
import { RcFile } from 'antd/lib/upload'
import styled from 'styled-components'

import { AttachmentBoxesFromUploader } from '../shared-components/AttachmentBox'

import { AttachmentUploader } from './AttachmentUploader'

import { Button, ButtonProps } from '@/components/shared-components/BUI/Button'
import { FileUploadResult } from '@/hooks/useAttachmentUpload'
import { MarkupModal } from '@/modules/defects/MarkupModal'
import { pluralise } from '@/utils/index'

import {
  AttachmentType,
  CreateAttachmentUiMutationVariables,
  Resource,
} from 'types/api.graphql'

const AttachFiles = styled(Button)`
  color: ${({ theme }) => theme.colour.secondary._500};
`

const FileCount = styled.span`
  ${({ theme }) => theme.font.label02}
  color: ${({ theme }) => theme.colour.secondary._500};
`

const Spacer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

type Props = {
  projectId: string
  attachmentResource: Resource
  attachmentSortDate?: string
  attachmentType: AttachmentType
  attachmentSize?: [number, number]
  enableMarkup?: boolean
  fileList?: FileUploadResult[]
  onChange?: (value: FileUploadResult[]) => void
  attachmentPosition?: 'bottom' | 'top'
  isPublic?: boolean
  mutationVariables?: Partial<CreateAttachmentUiMutationVariables['input']>
  showFileCount?: boolean
  buttonProps?: ButtonProps
  onUploadStatusChange?: (status: 'uploading' | 'done' | 'error') => void
  buttonText?: string
  icon?: React.ReactNode
  accept?: string
  children?:
    | React.ReactNode
    | ((fileList?: FileUploadResult[]) => React.ReactNode)
}

export const SimpleAttachmentUploader: React.FC<Props> = ({
  fileList,
  onChange,
  projectId,
  attachmentResource,
  attachmentSortDate,
  attachmentType,
  attachmentSize = [115, 90],
  enableMarkup = false,
  attachmentPosition = 'top',
  isPublic,
  mutationVariables,
  showFileCount = true,
  buttonProps,
  onUploadStatusChange,
  buttonText = 'Attach files',
  icon = <IconPaperclip size={16} />,
  accept,
}) => {
  const [markupFile, setMarkupFile] = useState<FileUploadResult>(null)

  const closeMarkupModal = () => setMarkupFile(null)

  return (
    <Spacer>
      <AttachmentUploader
        compact
        multiple
        maxCount={20}
        fileListPosition={attachmentPosition}
        fileList={fileList}
        onChange={onChange}
        isPublic={isPublic}
        projectId={projectId}
        mutationVariables={mutationVariables}
        attachmentResource={attachmentResource}
        attachmentSortDate={attachmentSortDate}
        attachmentType={attachmentType}
        accept={accept}
        renderFileList={(fileList, onRemove, insert) => (
          <>
            {fileList.length > 0 && showFileCount && (
              <FileCount>{pluralise(fileList.length, 'Attachment')}</FileCount>
            )}
            <AttachmentBoxesFromUploader
              width={attachmentSize[0]}
              height={attachmentSize[1]}
              files={fileList}
              onRemove={(file) => onRemove(file as RcFile)}
              onEdit={enableMarkup ? (file) => setMarkupFile(file) : undefined}
            />
            {markupFile && (
              <MarkupModal
                file={markupFile}
                closeModal={closeMarkupModal}
                insertFile={insert}
              />
            )}
          </>
        )}
        onUploadStatusChange={onUploadStatusChange}
      >
        {() => (
          <AttachFiles icon={icon} variant="secondary-text" {...buttonProps}>
            {buttonText}
          </AttachFiles>
        )}
      </AttachmentUploader>
    </Spacer>
  )
}
